import { isDeferredPlatformSlug } from 'constants/futureReleases';
import { ROUTES } from 'constants/routes';
import type { School, SchoolStatus } from 'types';

export type CatalogKind = 'school' | 'topic' | 'platform_feature';

export type CatalogRegistryEntry = {
  kind: CatalogKind;
  plannedTopicCount?: number;
  href?: string;
  forceStatus?: SchoolStatus;
};

const DEFAULT_ENTRY: CatalogRegistryEntry = { kind: 'school' };

/** Slug-keyed catalog taxonomy and platform overrides. */
export const PLATFORM_CATALOG_REGISTRY: Record<string, CatalogRegistryEntry> = {
  // Active schools of thought
  parashari: { kind: 'school' },
  jaimini: { kind: 'school' },
  kp: { kind: 'school' },
  bnn: { kind: 'school' },
  'lal-kitab': { kind: 'school' },
  numerology: { kind: 'school', plannedTopicCount: 51 },
  panchang: { kind: 'school', plannedTopicCount: 36 },
  vastu: { kind: 'school', plannedTopicCount: 56 },
  'swara-vigyan': { kind: 'school', plannedTopicCount: 56 },
  'yantra-vidya': { kind: 'school', plannedTopicCount: 25 },
  tantra: { kind: 'school', plannedTopicCount: 32 },
  'surya-siddhanta': { kind: 'school', plannedTopicCount: 24 },

  // Coming soon - schools of thought (divination / predictive systems)
  palmistry: { kind: 'school', plannedTopicCount: 40 },
  tajika: { kind: 'school', plannedTopicCount: 30 },
  western: { kind: 'school', plannedTopicCount: 36 },
  chinese: { kind: 'school', plannedTopicCount: 28 },
  tarot: { kind: 'school', plannedTopicCount: 22 },
  lenormand: { kind: 'school', plannedTopicCount: 18 },
  oracle: { kind: 'school', plannedTopicCount: 18 },
  runes: { kind: 'school', plannedTopicCount: 16 },
  'i-ching': { kind: 'school', plannedTopicCount: 20 },
  physiognomy: { kind: 'school', plannedTopicCount: 24 },
  samudrika: { kind: 'school', plannedTopicCount: 30 },
  'lo-shu': { kind: 'school', plannedTopicCount: 16 },
  horary: { kind: 'school', plannedTopicCount: 26 },
  ramala: { kind: 'school', plannedTopicCount: 20 },
  nimitta: { kind: 'school', plannedTopicCount: 22 },

  // Knowledge topics (IKS paths)
  sanskrit: { kind: 'topic', plannedTopicCount: 52 },
  ayurveda: { kind: 'topic', plannedTopicCount: 64 },
  yoga: { kind: 'topic', plannedTopicCount: 48 },
  vedanta: { kind: 'topic', plannedTopicCount: 40 },
  'bhagavad-gita': { kind: 'topic', plannedTopicCount: 36 },
  upanishads: { kind: 'topic', plannedTopicCount: 32 },
  'indian-philosophy': { kind: 'topic', plannedTopicCount: 44 },

  // Platform features
  'ai-tutor': {
    kind: 'platform_feature',
    forceStatus: 'active',
    href: ROUTES.AI_TUTOR,
  },
  community: {
    kind: 'platform_feature',
    forceStatus: 'active',
    href: ROUTES.COMMUNITY,
  },
  certifications: {
    kind: 'platform_feature',
    forceStatus: 'coming_soon',
    plannedTopicCount: 12,
    href: ROUTES.CERTIFICATES,
  },
};

export function getCatalogRegistryEntry(slug: string): CatalogRegistryEntry {
  return PLATFORM_CATALOG_REGISTRY[slug] ?? DEFAULT_ENTRY;
}

export function getCatalogKind(slug: string): CatalogKind {
  return getCatalogRegistryEntry(slug).kind;
}

export function resolveCatalogStatus(school: School): SchoolStatus {
  const entry = getCatalogRegistryEntry(school.slug);
  if (entry.forceStatus) return entry.forceStatus;
  return school.status;
}

export function isCatalogAvailable(school: School): boolean {
  return resolveCatalogStatus(school) === 'active';
}

export function getPlatformFeatureRoute(slug: string): string | undefined {
  return getCatalogRegistryEntry(slug).href;
}

export function getPlannedTopicCount(slug: string): number | undefined {
  return getCatalogRegistryEntry(slug).plannedTopicCount;
}

export type CatalogKindBuckets = {
  availableSchools: School[];
  platformFeatures: School[];
  comingSoonSchools: School[];
  comingSoonTopics: School[];
};

export function groupSchoolsByCatalogKind(schools: School[]): CatalogKindBuckets {
  const visible = schools.filter((s) => s.isActive !== false);
  const buckets: CatalogKindBuckets = {
    availableSchools: [],
    platformFeatures: [],
    comingSoonSchools: [],
    comingSoonTopics: [],
  };

  for (const school of visible) {
    const kind = getCatalogKind(school.slug);
    const available = isCatalogAvailable(school);

    if (kind === 'platform_feature') {
      if (isDeferredPlatformSlug(school.slug)) continue;
      buckets.platformFeatures.push({ ...school, status: resolveCatalogStatus(school) });
      continue;
    }

    if (kind === 'topic') {
      if (available) {
        buckets.availableSchools.push(school);
      } else {
        buckets.comingSoonTopics.push(school);
      }
      continue;
    }

    // school kind
    if (available) {
      buckets.availableSchools.push(school);
    } else {
      buckets.comingSoonSchools.push(school);
    }
  }

  return buckets;
}

export function getCatalogKindLabel(kind: CatalogKind): string {
  switch (kind) {
    case 'school':
      return 'School';
    case 'topic':
      return 'Topic';
    case 'platform_feature':
      return 'Feature';
    default:
      return 'School';
  }
}
